import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop'
import { Component, OnInit } from '@angular/core'
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap'
import { DiaryPreferencesService } from '../../services/diary-preferences.service'
import { Venue } from '@app/domain/Venue'
import { Question, sourceTypeOfQuestion } from '@app/domain/Question'

export type FixedDiaryListColumnKey = 'status' | 'time' | 'customer' | 'covers' | 'reason'
export type ConfigurableDiaryListColumnKey = 'area' | 'table' | 'details' | 'orderDetails' | 'customerMessage' | 'privateNotes' | 'dateCreated'
export type StandardDiaryListColumnKey = FixedDiaryListColumnKey | ConfigurableDiaryListColumnKey
export type DiaryListColumnKey = StandardDiaryListColumnKey | `question:${string}`

export interface DiaryListColumnOption {
    key: DiaryListColumnKey
    label: string
}

export function isQuestionColumnKey(key: string): key is `question:${string}` {
    return key.startsWith('question:')
}

export function getQuestionIdFromColumnKey(key: `question:${string}`): string {
    return key.replace('question:', '')
}

@Component({
    selector: 'app-diary-list-column-selection',
    templateUrl: './diary-list-column-selection.component.html',
    styles: [`
        :host
            display: flex
            flex-direction: column
            flex-grow: 1
            height: 100%
    `],
})
export class DiaryListColumnSelectionComponent implements OnInit {

    venue!: Venue

    fixedColumns: DiaryListColumnOption[] = [
        { key: 'status', label: 'Status' },
        { key: 'time', label: 'Time' },
        { key: 'customer', label: 'Customer' },
        { key: 'covers', label: 'Covers' },
        { key: 'reason', label: 'Reason' },
    ]

    configurableColumns: DiaryListColumnOption[] = [
        { key: 'area', label: 'Area' },
        { key: 'table', label: 'Table' },
        { key: 'details', label: 'Details' },
        { key: 'orderDetails', label: 'Order Details' },
        { key: 'customerMessage', label: 'Customer Message' },
        { key: 'privateNotes', label: 'Private Notes' },
        { key: 'dateCreated', label: 'Date Created' },
    ]

    selectedColumns: DiaryListColumnOption[] = []

    constructor(
        private diaryPreferencesService: DiaryPreferencesService,
        private offcanvas: NgbActiveOffcanvas
    ) { }

    ngOnInit() {
        this.loadPreferences()
    }

    dismiss() {
        this.offcanvas.dismiss()
    }

    save() {
        const allColumns = this.selectedColumns.map(column => column.key)
        this.diaryPreferencesService.setListViewColumns(allColumns)
        this.offcanvas.close(allColumns)
    }

    isColumnSelected(columnKey: DiaryListColumnKey): boolean {
        return this.selectedColumns.some(column => column.key === columnKey)
    }

    dropColumn(event: CdkDragDrop<DiaryListColumnOption[]>) {
        if (event.previousIndex === event.currentIndex) {
            return
        }
        const columns = [...this.selectedColumns]
        moveItemInArray(columns, event.previousIndex, event.currentIndex)
        this.selectedColumns = columns
    }

    get availableQuestions(): Question[] {
        if (!this.venue) {
            return []
        }
        const selectedQuestionIds = this.selectedColumns
            .map(column => column.key)
            .filter((key): key is `question:${string}` => isQuestionColumnKey(key))
            .map(key => getQuestionIdFromColumnKey(key))
        return this.venue.questions.filter(q => {
            const source = sourceTypeOfQuestion(q)
            return source === 'merchant' && !selectedQuestionIds.includes(q.id)
        })
    }

    get availableOptionalColumns(): DiaryListColumnOption[] {
        return this.configurableColumns.filter(column => !this.isColumnSelected(column.key))
    }

    addOptionalColumn(event: Event) {
        const target = event.target as HTMLSelectElement
        const columnKey = target.value as ConfigurableDiaryListColumnKey
        if (!columnKey) {
            return
        }

        const column = this.configurableColumns.find(configurableColumn => configurableColumn.key === columnKey) ?? null
        this.addColumn(column)
        this.resetSelect(target)
    }

    addQuestionColumn(event: Event) {
        const target = event.target as HTMLSelectElement
        const questionId = target.value
        if (!questionId || !this.venue) {
            return
        }
        const question = this.venue.questions.find(q => q.id === questionId)
        this.addColumn(question ? { key: `question:${question.id}`, label: question.bookingDisplayText } : null)
        this.resetSelect(target)
    }

    removeColumn(columnKey: DiaryListColumnKey) {
        if (this.isFixedColumn(columnKey)) {
            return
        }
        this.selectedColumns = this.selectedColumns.filter(column => column.key !== columnKey)
    }

    isFixedColumn(columnKey: DiaryListColumnKey): boolean {
        return this.fixedColumns.some(column => column.key === columnKey)
    }

    private loadPreferences() {
        const storedColumns = this.diaryPreferencesService.getListViewColumns()
        this.selectedColumns = storedColumns
            .map(columnKey => this.getColumnOption(columnKey))
            .filter((column): column is DiaryListColumnOption => column !== null)
    }

    private addColumn(column: DiaryListColumnOption | null) {
        if (!column || this.isColumnSelected(column.key)) {
            return
        }

        this.selectedColumns = [...this.selectedColumns, column]
    }

    private resetSelect(select: HTMLSelectElement) {
        select.value = ''
    }

    private getColumnOption(columnKey: DiaryListColumnKey): DiaryListColumnOption | null {
        if (isQuestionColumnKey(columnKey)) {
            const questionId = getQuestionIdFromColumnKey(columnKey)
            const question = this.venue?.questions.find(q => q.id === questionId)
            return question ? { key: columnKey, label: question.bookingDisplayText } : null
        }

        return [...this.fixedColumns, ...this.configurableColumns].find(column => column.key === columnKey) ?? null
    }
}
